import type { Menu } from '../service/menu/types';

// 全局状态接口
export interface GlobalState {
  menuList: Menu.Menu[];
  userInfo: any;
  isInitialized: boolean;
}

// 全局状态管理器
class GlobalStore {
  private state: GlobalState = {
    menuList: this.loadMenuListFromStorage(),
    userInfo: this.loadUserInfoFromStorage(),
    isInitialized: this.loadInitializedFromStorage()
  };

  private listeners: Set<() => void> = new Set();

  // 从localStorage加载菜单列表
  private loadMenuListFromStorage(): Menu.Menu[] {
    try {
      const stored = localStorage.getItem('globalMenuList');
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('加载菜单列表失败:', error);
      return [];
    }
  }

  // 从localStorage加载用户信息
  private loadUserInfoFromStorage(): any {
    try {
      const stored = localStorage.getItem('globalUserInfo');
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('加载用户信息失败:', error);
      return null;
    }
  }

  // 从localStorage加载初始化状态
  private loadInitializedFromStorage(): boolean {
    try {
      const stored = localStorage.getItem('globalInitialized');
      return stored === 'true';
    } catch (error) {
      console.error('加载初始化状态失败:', error);
      return false;
    }
  }

  // 保存菜单列表到localStorage
  private saveMenuListToStorage(menuList: Menu.Menu[]): void {
    try {
      localStorage.setItem('globalMenuList', JSON.stringify(menuList));
    } catch (error) {
      console.error('保存菜单列表失败:', error);
    }
  }

  // 保存用户信息到localStorage
  private saveUserInfoToStorage(userInfo: any): void {
    try {
      localStorage.setItem('globalUserInfo', JSON.stringify(userInfo));
    } catch (error) {
      console.error('保存用户信息失败:', error);
    }
  }

  // 保存初始化状态到localStorage
  private saveInitializedToStorage(initialized: boolean): void {
    try {
      localStorage.setItem('globalInitialized', initialized.toString());
    } catch (error) {
      console.error('保存初始化状态失败:', error);
    }
  }

  // 获取当前状态
  getState(): GlobalState {
    return { ...this.state };
  }

  // 设置菜单列表
  setMenuList(menuList: Menu.Menu[]): void {
    this.state.menuList = menuList;
    this.saveMenuListToStorage(menuList);
    this.notifyListeners();
  }

  // 设置用户信息（使用合并策略，避免覆盖关键字段如 roles、permissions）
  setUserInfo(userInfo: any): void {
    // 如果当前已有用户信息，使用合并策略保留关键字段
    if (this.state.userInfo && userInfo) {
      this.state.userInfo = {
        ...this.state.userInfo, // 先展开旧数据（包含 roles、permissions 等）
        ...userInfo              // 再覆盖新数据（比如只更新了昵称、邮箱等）
      };
    } else {
      // 如果没有现有数据，直接使用新数据
      this.state.userInfo = userInfo;
    }
    this.saveUserInfoToStorage(this.state.userInfo);
    this.notifyListeners();
  }

  // 设置初始化状态
  setInitialized(initialized: boolean): void {
    this.state.isInitialized = initialized;
    this.saveInitializedToStorage(initialized);
    this.notifyListeners();
  }

  // 清除所有数据
  clearAll(): void {
    this.state = {
      menuList: [],
      userInfo: null,
      isInitialized: false
    };
    // 清除localStorage中的数据
    try {
      localStorage.removeItem('globalMenuList');
      localStorage.removeItem('globalUserInfo');
      localStorage.removeItem('globalInitialized');
    } catch (error) {
      console.error('清除localStorage失败:', error);
    }
    this.notifyListeners();
  }

  // 订阅状态变化
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  // 通知所有监听器
  private notifyListeners(): void {
    this.listeners.forEach(listener => listener());
  }

  // 获取菜单列表
  getMenuList(): Menu.Menu[] {
    return [...this.state.menuList];
  }

  // 获取用户信息
  getUserInfo(): any {
    return this.state.userInfo;
  }

  // 获取权限列表
  getPermissions(): string[] {
    return this.state.userInfo?.permissions || [];
  }

  // 检查是否已初始化
  isInitialized(): boolean {
    return this.state.isInitialized;
  }
}

// 创建全局单例实例
export const globalStore = new GlobalStore();

